import { initializeEmptyKeypoints } from '../utils/cocoFormat'

// Use API URL from environment variable
const CV_CORE_API_URL = import.meta.env.VITE_CV_CORE_API_URL

// For debugging: log the current API URL
if (import.meta.env.DEV) {
  console.log('CV Core API URL:', CV_CORE_API_URL)
}

interface PredictionResponse {
  keypoints: number[][] // Array of [x, y] coordinates
  confidence: number[] // Confidence values for each keypoint
  skeleton: number[][] // Skeleton connections
}

// Helper function to crop image to bounding box
async function cropImageToBbox(imageUrl: string, bbox: number[]): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      // Create canvas for cropped image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Set canvas size to bbox dimensions
      canvas.width = bbox[2]
      canvas.height = bbox[3]

      // Draw cropped region
      ctx.drawImage(
        img,
        bbox[0],
        bbox[1],
        bbox[2],
        bbox[3], // Source rectangle
        0,
        0,
        bbox[2],
        bbox[3] // Destination rectangle
      )

      // Convert to blob
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob'))
        }
      }, 'image/jpeg')
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    img.src = imageUrl
  })
}

export async function predictKeypoints(
  imageUrl: string,
  bbox?: number[]
): Promise<PredictionResponse> {
  try {
    let imageBlob: Blob

    if (bbox && bbox.length === 4) {
      // Crop image to bounding box
      imageBlob = await cropImageToBbox(imageUrl, bbox)
    } else {
      // Use full image
      const imageResponse = await fetch(imageUrl)
      if (!imageResponse.ok) {
        throw new Error('Failed to fetch image')
      }
      imageBlob = await imageResponse.blob()
    }

    // Create form data
    const formData = new FormData()
    formData.append('image', imageBlob, 'image.jpg')

    // Call the prediction API
    const response = await fetch(`${CV_CORE_API_URL}/predict/image`, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Prediction failed')
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error predicting keypoints:', error)
    throw error
  }
}

export function convertPredictionToKeypoints(
  prediction: PredictionResponse,
  keypointCount: number,
  bbox?: number[]
) {
  const keypoints = initializeEmptyKeypoints(keypointCount)
  const CONFIDENCE_THRESHOLD = 0.45

  // Apply predicted keypoints
  prediction.keypoints.forEach((coord, index) => {
    if (index < keypoints.length && coord.length >= 2) {
      const confidence = prediction.confidence[index] || 0

      // Only add keypoints with confidence above threshold
      if (confidence >= CONFIDENCE_THRESHOLD) {
        let x = coord[0]
        let y = coord[1]

        // If bbox is provided, convert coordinates from bbox-relative to image-relative
        if (bbox && bbox.length === 4) {
          x = bbox[0] + x
          y = bbox[1] + y
        }

        keypoints[index] = {
          x: x,
          y: y,
          confidence: confidence,
          visibility: 'visible',
        }
      }
      // If confidence is below threshold, keep the keypoint unset (x: -1, y: -1)
    }
  })

  return keypoints
}
